import { useMemo, useState } from 'react';

import { format } from 'date-fns';
import styled from 'styled-components';

import { Badge } from '@components/badge';
import { Button } from '@components/button';
import { ButtonGroup } from '@components/button-group';
import { Card } from '@components/card';
import { FormValidatorType, createForm, useCreateFormStore } from '@components/form';
import { FormLabel } from '@components/form-label';
import { useInfoModal } from '@components/generic-modals/info-modal';
import { IconAddPlus } from '@components/icons/add-plus';
import { IconArrowLeft } from '@components/icons/arrow-left';
import { IconArrowRight } from '@components/icons/arrow-right';
import { IconFormIndicatorWarning } from '@components/icons/form-indicator-warning';
import { IconJourneyItem } from '@components/icons/journey-icon';
import { IconPerson } from '@components/icons/person';
import { IconTraveller } from '@components/icons/traveller';
import { ListCard } from '@components/list-card';
import { RelativeSpinner } from '@components/loading-spinner';
import { SearchBox } from '@components/search-box';
import { SelectOption } from '@components/select';
import { Sidebar, hideSidebar, useSidebar } from '@components/sidebar';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import {
  FieldMappingEditorValue,
  FieldMappingGroup,
} from '@components/text-editors/addons/field-mapping';
import { ValueList } from '@components/value-list';
import { styledTranslate, useScopedTranslate } from '@locale/translate';
import { theme } from '@styles/theme';
import { pxToRem } from '@utils/converters';
import { getKeys } from '@utils/objects';

import { AddToJourneyType, AttributeType, JourneyStatsType, JourneyType } from './api';
import { useVariableGroups } from './helpers';
import {
  useAddToJourney,
  useCompanyTravellerAttributes,
  useJourneyTravllerAttributes,
  useJourneys,
  useJourneysStats,
  useList,
  useToken,
  useUsers,
} from './hooks';

export const useAddToJourneySidebar = () => {
  const sidebar = useSidebar(AddToJourney, {
    variant: 'REGULAR',
    hasBackdrop: true,
  });
  return sidebar;
};

export const AddToJourney = ({
  applications,
  jobId,
}: {
  applications: number[];
  jobId: number;
}) => {
  const { t } = useScopedTranslate(locale);
  const [journey, setJourney] = useState<JourneyType>();
  const [search, setSearch] = useState('');
  const tokenQuery = useToken();
  const isConnected = tokenQuery.data?.ok && tokenQuery.data.valid;

  return (
    <Sidebar>
      <Sidebar.SimpleHeader
        title={t('addToJourney')}
        icon={IconAddPlus}
        iconColor="blue100"
        extraHeader={
          !journey ? <SearchBox onChange={setSearch} placeholder={t('search')} /> : undefined
        }
        hideExtraHeaderSeparator
      />
      <Sidebar.Content>
        {tokenQuery.isLoading ? (
          <RelativeSpinner />
        ) : !isConnected ? (
          <InvalidCredentials />
        ) : journey ? (
          <JourneyDetails
            journey={journey}
            clearJourney={() => setJourney(undefined)}
            applications={applications}
            jobId={jobId}
          />
        ) : (
          <SelectJourney setJourney={setJourney} search={search} />
        )}
      </Sidebar.Content>
    </Sidebar>
  );
};

const InvalidCredentials = () => {
  const { t } = useScopedTranslate(locale);
  return (
    <Card space={3} variant="orange">
      <Stack space={2} orientation="horizontal" align="center">
        <IconFormIndicatorWarning />
        <Text font="regular14" color="orange200" text={t('invalidCredentialsWarning')} />
      </Stack>
    </Card>
  );
};

type SelectJourneyType = {
  setJourney: (journey: JourneyType) => void;
  search: string;
};
const SelectJourney = ({ setJourney, search }: SelectJourneyType) => {
  const { t } = useScopedTranslate(locale);
  const journeysQuery = useJourneys();
  const statsQuery = useJourneysStats();
  const statistics = statsQuery.data ?? [];
  const filteredJourneysList = useMemo(() => {
    return (journeysQuery.data ?? []).filter(
      (journey) =>
        !search ||
        journey.title.toUpperCase().includes(search.toUpperCase()) ||
        (journey.description ?? '').toUpperCase().includes(search.toUpperCase())
    );
  }, [journeysQuery.data, search]);

  if (journeysQuery.isLoading) {
    return <RelativeSpinner />;
  }

  function getJourneyStats(journeyId: string): JourneyStatsType | undefined {
    return statistics.find((stats) => stats.shortId === journeyId);
  }

  return (
    <Stack space={2}>
      <Text font="semibold14" text={t('selectJourney')} />
      {filteredJourneysList.map((journey) => (
        <CardContainer onClick={() => setJourney(journey)} key={journey.shortId}>
          <JourneyCard journey={journey} statistics={getJourneyStats(journey.shortId)} />
        </CardContainer>
      ))}
    </Stack>
  );
};

const JourneyCard = ({
  journey,
  statistics,
}: {
  journey: JourneyType;
  statistics?: JourneyStatsType;
}) => {
  const { t } = useScopedTranslate(locale);

  return (
    <ListCard
      key={journey.shortId}
      title={journey.title}
      description={journey.description || t('noDescription')}
      spaceX={3}
      spaceY={3}
      icon={
        <IconContainer>
          <IconJourneyItem />
        </IconContainer>
      }
      actions={
        <>
          {statistics && (
            <ValueList
              items={[
                { value: statistics.numTravellers, label: t('travellers') },
                { value: statistics.numNodeActions, label: t('actions') },
              ]}
            />
          )}
        </>
      }
    />
  );
};

const CardContainer = styled.div({
  cursor: 'pointer',
});

const IconContainer = styled.div({
  height: pxToRem(40),
  width: pxToRem(40),
  borderRadius: pxToRem(8),
  backgroundColor: theme.color.purpleTrans5,
  border: `${pxToRem(1)} solid ${theme.color.purpleTrans10}`,
  display: 'flex',
  padding: pxToRem(8),
  alignItems: 'center',
  justifyContent: 'center',
});

type JourneyDetailsType = {
  journey: JourneyType;
  clearJourney: () => void;
  applications: number[];
  jobId: number;
};

type CompanyTravellerBaseFields = {
  name: string;
  email: string;
  phone: string;
};
type TravellerJourneyAttributes = {
  [K in `journey_attribute__${string}`]: string;
};

type CompanyTravellerAttributes = {
  [K in `company_traveller_attribute__${string}`]: string;
};
type Attributes = TravellerJourneyAttributes & CompanyTravellerAttributes;
type FormData = { startJourney: boolean } & CompanyTravellerBaseFields & Attributes;

const Form = createForm<FormData>();

const JourneyDetails = ({ journey, clearJourney, applications, jobId }: JourneyDetailsType) => {
  const { t } = useScopedTranslate(locale);
  const formStore = useCreateFormStore();
  const formValues = formStore.useFormValues();
  const isStartJourneyEnabled = formValues['startJourney'];
  const showInfoModal = useInfoModal();
  const companyTravellerAttributesQuery = useCompanyTravellerAttributes();
  const companyTravellerAttributes = (companyTravellerAttributesQuery.data ?? []).filter(
    (attribute) => attribute.isActive
  );
  const journeyTravellerAttributesQuery = useJourneyTravllerAttributes(journey.shortId);
  const journeyTravellerAttributes = (journeyTravellerAttributesQuery.data ?? []).filter(
    (attribute) => attribute.isActive
  );
  const fieldMappingId = `journey-${journey.shortId}`;
  const usersQuery = useUsers();
  const users = (usersQuery.data ?? []).map((user) => ({ value: user.shortId, label: user.name }));
  const handleSuccess = () => {
    if (applications.length > 1) {
      showInfoModal({
        title: t('adding'),
        children: (
          <div>
            <Text text={t('actionBeingProcessed')} />
            <Text text={t('mightTakeFewMinutes')} color="textSecondary" />
          </div>
        ),
      });
    }
    hideSidebar();
  };
  const addToJourneyMutation = useAddToJourney({ onSuccess: handleSuccess });

  const isLoading =
    companyTravellerAttributesQuery.isLoading ||
    journeyTravellerAttributesQuery.isLoading ||
    usersQuery.isLoading;

  const baseVariableGroups = useVariableGroups({ jobId: jobId });
  const emailVariableGroups = useVariableGroups({ jobId: jobId, types: ['email'] });
  const phoneVariableGroups = useVariableGroups({ jobId: jobId, types: ['phone'] });
  const fileVariableGroups = useVariableGroups({ jobId: jobId, types: ['file'] });
  const fileMultiVariableGroups = useVariableGroups({
    jobId: jobId,
    types: ['file_multi', 'file'],
  });

  const handleSave = (data: FormData) => {
    if (!data) return;
    const attributes: { [key: string]: string } = {};
    const keys = getKeys(data);
    for (const key of keys) {
      if (key === 'startJourney') {
        continue;
      }
      const value = data[key] ?? '';

      const attribute = key.startsWith('company_traveller_attribute__')
        ? companyTravellerAttributes.find(
            (attr) => attr.shortId === key.replace('company_traveller_attribute__', '')
          )
        : journeyTravellerAttributes.find(
            (attr) => attr.shortId === key.replace('journey_attribute__', '')
          );

      if (!attribute) {
        continue;
      }
      if (attribute.attributeType === 'date') {
        attributes[key] = value ? format(new Date(value), 'yyyy-MM-dd') : '';
      } else {
        attributes[key] = value;
      }
    }
    const item: AddToJourneyType = {
      journey: journey.shortId,
      applications: applications,
      name: data.name,
      email: data.email,
      phone: data.phone,
      startJourney: data.startJourney,
      attributes,
    };

    addToJourneyMutation.mutateAsync(item);
  };

  if (isLoading) {
    return <RelativeSpinner />;
  }
  const nameDefault: FieldMappingEditorValue = {
    value: '{{ application_name }}',
    variables: {
      application_name: {
        source: 'application',
        field: 'name',
        label: t('applicantFullName'),
        type: 'text',
      },
    },
  };

  const emailDefault: FieldMappingEditorValue = {
    value: '{{ application_email }}',
    variables: {
      application_email: {
        source: 'application',
        field: 'email',
        label: t('applicationEmail'),
        type: 'email',
      },
    },
  };

  const phoneDefault: FieldMappingEditorValue = {
    value: '{{ application_phone }}',
    variables: {
      application_phone: {
        source: 'application',
        field: 'phone',
        label: t('applicationPhone'),
        type: 'phone',
      },
    },
  };

  return (
    <Form store={formStore} onSubmit={handleSave}>
      <Stack space={6}>
        <Stack orientation="horizontal" justify="space-between">
          <Stack space={2}>
            <Stack orientation="horizontal" space={2}>
              <IconPerson color="purple100" />
              <Text font="bold14" text={t('applicantsSelected')} />
            </Stack>
            <Stack orientation="horizontal" space={2}>
              <IconArrowRight color="blue100" />
              <Text font="bold14" text={journey.title} />
            </Stack>
          </Stack>
          <Badge children={applications.length} size="md" variant="secondary" />
        </Stack>
        <Sidebar.Separator />
        <Stack space={4}>
          <Stack space={2} orientation="horizontal">
            <IconTraveller color="purple100" />
            <Text font="bold14" text={t('journeyFields')} />
          </Stack>
          <Text
            font="regular14"
            text={styledTranslate({
              key: 'journeyFieldsDescription',
              variables: {
                journeyTitle: {
                  text: `"${journey.title}"`,
                  font: 'bold14',
                },
              },
              translations: locale,
            })}
          />
          <Form.FieldMappingTextEditor
            id="name"
            label={t('name')}
            validators={['required']}
            singleLine
            plaintext
            variableGroups={baseVariableGroups}
            defaultValue={nameDefault}
            fieldMappingId={fieldMappingId}
          />
          <Form.EmailField
            id="email"
            label={t('email')}
            validators={['required']}
            variableGroups={emailVariableGroups}
            defaultValue={emailDefault}
            fieldMappingId={fieldMappingId}
          />
          <Form.FieldMappingPhoneField
            id="phone"
            label={t('phone')}
            variableGroups={phoneVariableGroups}
            defaultValue={phoneDefault}
            fieldMappingId={fieldMappingId}
            fieldMapText={{
              title: t('fieldMapTitle'),
              description: t('fieldMapDescription'),
            }}
          />
          {companyTravellerAttributes.map((attribute) => (
            <AttributeField
              key={attribute.shortId}
              attribute={attribute}
              isCompanyTravellerAttribute
              users={users}
              baseVariableGroups={baseVariableGroups}
              emailVariableGroups={emailVariableGroups}
              phoneVariableGroups={phoneVariableGroups}
              fileVariableGroups={fileVariableGroups}
              fileMultiVariableGroups={fileMultiVariableGroups}
              fieldMappingId={fieldMappingId}
            />
          ))}
        </Stack>
        {journeyTravellerAttributes.length > 0 && (
          <>
            <Sidebar.Separator />
            <Stack space={4}>
              {journeyTravellerAttributes.map((attribute) => (
                <AttributeField
                  key={attribute.shortId}
                  attribute={attribute}
                  users={users}
                  baseVariableGroups={baseVariableGroups}
                  emailVariableGroups={emailVariableGroups}
                  phoneVariableGroups={phoneVariableGroups}
                  fileVariableGroups={fileVariableGroups}
                  fileMultiVariableGroups={fileMultiVariableGroups}
                  fieldMappingId={fieldMappingId}
                />
              ))}
            </Stack>
          </>
        )}
        <Sidebar.Separator />
        <Stack orientation="horizontal" justify="space-between">
          <Stack>
            <FormLabel text={t('startJourneyWhenAdded')} />
            {isStartJourneyEnabled ? (
              <Text text={t('core.yes')} />
            ) : (
              <Text color="textHelper" text={t('core.no')} />
            )}
          </Stack>
          <Form.Switch
            id="startJourney"
            label={t('startJourneyWhenAdded')}
            labelVisible={false}
            defaultValue={false}
          />
        </Stack>
        <Sidebar.Footer>
          <Stack orientation="horizontal" justify="space-between">
            <Button
              variant="secondary"
              iconLeft={<IconArrowLeft />}
              text={t('core.back')}
              onClick={clearJourney}
            />
            <ButtonGroup align="right">
              <Button variant="secondary" text={t('core.cancel')} onClick={hideSidebar} />
              <Form.Submit
                variant="primary-blue"
                text={t('addToJourney')}
                disabled={addToJourneyMutation.isLoading}
              />
            </ButtonGroup>
          </Stack>
        </Sidebar.Footer>
      </Stack>
    </Form>
  );
};

type CommonFieldProps = {
  id: keyof Attributes;
  label: string;
  validators?: FormValidatorType[];
  fieldMappingId: string;
};

const AttributeField = ({
  attribute,
  isCompanyTravellerAttribute = false,
  users,
  baseVariableGroups,
  emailVariableGroups,
  phoneVariableGroups,
  fileVariableGroups,
  fileMultiVariableGroups,
  fieldMappingId,
}: {
  attribute: AttributeType;
  value?: any;
  isCompanyTravellerAttribute?: boolean;
  users: SelectOption[];
  baseVariableGroups: FieldMappingGroup[];
  emailVariableGroups: FieldMappingGroup[];
  phoneVariableGroups: FieldMappingGroup[];
  fileVariableGroups: FieldMappingGroup[];
  fileMultiVariableGroups: FieldMappingGroup[];
  fieldMappingId: CommonFieldProps['fieldMappingId'];
}) => {
  const { t } = useScopedTranslate(locale);
  const listQuery = useList(attribute.options ?? '');

  const fieldId: keyof Attributes = isCompanyTravellerAttribute
    ? `company_traveller_attribute__${attribute.shortId}`
    : `journey_attribute__${attribute.shortId}`;

  const commonFieldProps: CommonFieldProps = {
    id: fieldId,
    label: attribute.title,
    validators: attribute.isRequired ? ['required'] : [],
    fieldMappingId: fieldMappingId,
  };
  switch (attribute.attributeType) {
    case 'text':
      return (
        <Form.FieldMappingTextEditor
          {...commonFieldProps}
          singleLine
          plaintext
          variableGroups={baseVariableGroups}
        />
      );
    case 'paragraph':
      return (
        <Form.FieldMappingTextEditor {...commonFieldProps} variableGroups={baseVariableGroups} />
      );
    case 'date':
      return <Form.DatePicker {...commonFieldProps} placeholder="DD / MM / YYYY" />;
    case 'user':
      return <Form.Select {...commonFieldProps} options={users} />;
    case 'phone_number':
      return (
        <Form.FieldMappingPhoneField
          {...commonFieldProps}
          variableGroups={phoneVariableGroups}
          fieldMapText={{
            title: t('fieldMapTitle'),
            description: t('fieldMapDescription'),
          }}
        />
      );
    case 'email':
      return <Form.EmailField {...commonFieldProps} variableGroups={emailVariableGroups} />;
    case 'single_select':
      return (
        <Form.Select
          {...commonFieldProps}
          options={(listQuery.data?.options ?? []).map((option) => ({
            value: option.shortId,
            label: option.label,
          }))}
        />
      );
    case 'file':
      return (
        <Form.FieldMappingFileField
          {...commonFieldProps}
          variableGroups={fileVariableGroups}
          allowUploadFiles={false}
        />
      );
    case 'file_multi':
      return (
        <Form.FieldMappingFileListField
          {...commonFieldProps}
          variableGroups={fileMultiVariableGroups}
          allowUploadFiles={false}
        />
      );
    default:
      return null;
  }
};

const locale = {
  en: {
    addToJourney: 'Add to journey',
    selectJourney: 'Select journey',
    noDescription: 'No description',
    travellers: 'Travellers',
    actions: 'Actions',
    applicantsSelected: 'Applicant/s Selected',
    journeyFields: 'Journey fields',
    journeyFieldsDescription:
      'The fields below are used in the journey {journeyTitle}. Fill out the relevant fields before adding your applicants to the journey.',
    name: 'Name',
    email: 'Email',
    phone: 'Phone',
    search: 'Search',
    applicationEmail: 'Applicant email',
    applicationPhone: 'Applicant phone',
    applicantFullName: 'Applicant full name',
    adding: 'Adding',
    actionBeingProcessed: 'This action is being processed',
    mightTakeFewMinutes: 'Please note this might take a few minutes',
    invalidCredentialsWarning:
      'Your credentials have either expired or been removed, please reconnect to re-enable this integration',
    fieldMapTitle: 'Field map',
    fieldMapDescription: 'Receive value from a field in the application',
    startJourneyWhenAdded: 'Start journey when added',
  },
  is: {
    addToJourney: 'Senda í journey',
    selectJourney: 'Velja journey',
    noDescription: 'Engin lýsing',
    travellers: 'Travellers',
    actions: 'Actions',
    applicantsSelected: 'Valdnir umsækjendur',
    journeyFields: 'Journey upplýsingar',
    journeyFieldsDescription:
      'Upplýsingarnar hér fyrir neðan eru notaðar í {journeyTitle}. Fylltu út viðeigandi reiti áður en þú sendir umsækjendur í valið journey',
    name: 'Nafn',
    email: 'Netfang',
    phone: 'Sími',

    search: 'Leita',
    applicationEmail: 'Netfang umsækjanda',
    applicationPhone: 'Sími umsækjanda',
    applicantFullName: 'Fullt nafn umsækjanda',
    adding: 'Bæti við',
    actionBeingProcessed: 'Þessi aðgerð er í vinnslu',
    mightTakeFewMinutes: 'Vinsamlegast athugaðu að þetta gæti tekið nokkrar mínútur',
    invalidCredentialsWarning:
      'Þínar aðgangssupplýsingar eru útrunnar eða hafa verið fjarlægðar, vinsamlegast setjið upp tenginguna aftur',
    fieldMapTitle: 'Ráðningargögn',
    fieldMapDescription: 'Nota gildi úr ráðningargögnum',
    startJourneyWhenAdded: 'Hefja journey sjálfvirkt',
  },
};
